import { useEffect, useState } from 'react'
import { CheckCircle, XCircle, Loader2, AlertTriangle, MessageSquare, Clock, ArrowRight, Send } from 'lucide-react'
import { dealsApi, getAuthHeaders } from '@api/client'
import { useAuthStore } from '@store/authStore'
import { showToast } from '@store/uiStore'
import { formatCurrency, getRelativeTime } from '@utils/format'
import { useSubmitForReview } from '../hooks/useSubmitForReview'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

interface ImplReviewSnapshot {
  feeBefore: number | null
  feeAfter: number | null
  completedAt: string | null
  completedByName: string | null
}

interface ReviewBannerProps {
  dealId: string
  customerName: string
  dealStatus: string
  onStatusChange: (newStatus: string) => void
  submitterEmail?: string
  submitterName?: string
  // PR 3 (impl-review): banner state machine. NULL impl_review_status falls through to
  // the standard in_review banner.
  implReviewStatus?: string | null
  implReviewRequestedAt?: string | null
  implReviewRequestedBy?: string | null
  implReviewSnapshot?: ImplReviewSnapshot | null
  /** Called after the S3 "continue" submit succeeds so the parent re-fetches the deal. */
  onContinued?: () => void
}

export function ReviewBanner({ dealId, customerName, dealStatus: _dealStatus, onStatusChange, submitterEmail, submitterName, implReviewStatus, implReviewRequestedAt, implReviewRequestedBy, implReviewSnapshot, onContinued }: ReviewBannerProps) {
  const { user } = useAuthStore()
  const { submit: submitForReview, isSubmitting: isContinuing } = useSubmitForReview()
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [reviewNotes, setReviewNotes] = useState('')
  const [showNotes, setShowNotes] = useState(false)
  const [actionComplete, setActionComplete] = useState<'approved' | 'rejected' | null>(null)

  // Hold the banner in its "completed" state for a few seconds after the
  // reviewer takes action, then notify the parent so it can flip dealStatus
  // and re-render the page in the new mode. Without this delay the parent
  // would unmount the banner the instant the action completes, and the
  // reviewer would never see the "Quote approved and finalized" / "Quote
  // sent back for revision" confirmation.
  // Cleanup clears the timer if the user navigates away mid-hold.
  useEffect(() => {
    if (!actionComplete) return
    const targetStatus = actionComplete === 'approved' ? 'finalized' : 'draft'
    const t = setTimeout(() => onStatusChange(targetStatus), 3000)
    return () => clearTimeout(t)
  }, [actionComplete, onStatusChange])

  const sendReviewCompleteNotification = async (action: 'approved' | 'rejected', notes: string | null) => {
    try {
      const baseUrl = window.location.origin
      const quoteUrl = `${baseUrl}/quote?session=${dealId}`

      const res = await fetch(`${API_BASE}/slack/review-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          customerName,
          dealId: Number(dealId),
          action,
          reviewedBy: user?.email || 'unknown',
          reviewNotes: notes,
          submitterEmail: submitterEmail || '',
          submitterName: submitterName || '',
          quoteUrl,
        }),
      })

      const result = await res.json() as { success: boolean; error?: string }
      if (!result.success) {
        console.warn('Review notification issue:', result.error)
        showToast(`Quote ${action} successfully, but Slack notification to ${submitterName || 'the submitter'} may not have been delivered: ${result.error || 'unknown error'}`, 'warning', 8000)
      }
    } catch (error) {
      console.error('Failed to send review completion notification:', error)
      showToast(`Quote ${action} successfully, but failed to send Slack notification to ${submitterName || 'the submitter'}.`, 'warning', 6000)
    }
  }

  const handleApprove = async () => {
    setIsApproving(true)
    try {
      // Save review metadata first
      const updateResponse = await dealsApi.update(Number(dealId), {
        reviewed_by: user?.email || 'unknown',
        reviewed_at: new Date().toISOString(),
        review_notes: reviewNotes || null,
      } as any)

      if (!updateResponse.success) {
        showToast(updateResponse.error || 'Failed to save review details', 'error')
        return
      }

      // Finalize via dedicated endpoint (server uses auth user's identity)
      const finalizeResponse = await dealsApi.finalize(Number(dealId), user?.email || 'unknown')

      if (finalizeResponse.success) {
        setActionComplete('approved')
        // onStatusChange is deferred — see the useEffect below. We hold the
        // banner in its success state for a few seconds before letting the
        // parent flip dealStatus and unmount us, so the reviewer actually
        // sees the "Quote approved and finalized" confirmation instead of
        // an instant page-mode flip.
        sendReviewCompleteNotification('approved', reviewNotes || null)
      } else {
        showToast(finalizeResponse.error || 'Failed to approve quote', 'error')
      }
    } catch (error) {
      console.error('Approve error:', error)
      showToast('Failed to approve quote. Please try again.', 'error')
    } finally {
      setIsApproving(false)
    }
  }

  const handleReject = async () => {
    if (!reviewNotes.trim()) {
      showToast('Please add review notes explaining why the quote is being sent back.', 'warning')
      setShowNotes(true)
      return
    }

    setIsRejecting(true)
    try {
      const response = await dealsApi.update(Number(dealId), {
        status: 'draft',
        reviewed_by: user?.email || 'unknown',
        reviewed_at: new Date().toISOString(),
        review_notes: reviewNotes,
      } as any)

      if (response.success) {
        setActionComplete('rejected')
        // onStatusChange is deferred — see useEffect below.
        sendReviewCompleteNotification('rejected', reviewNotes)
      } else {
        showToast(response.error || 'Failed to send back quote', 'error')
      }
    } catch (error) {
      console.error('Reject error:', error)
      showToast('Failed to send back quote. Please try again.', 'error')
    } finally {
      setIsRejecting(false)
    }
  }

  // Show completed state
  if (actionComplete) {
    return (
      <div
        className={`rounded-lg p-4 border ${
          actionComplete === 'approved'
            ? 'bg-success/10 border-success/30'
            : 'bg-warning/10 border-warning/30'
        }`}
      >
        <div className="flex items-center gap-3">
          {actionComplete === 'approved' ? (
            <CheckCircle size={20} className="text-success" />
          ) : (
            <AlertTriangle size={20} className="text-warning" />
          )}
          <div>
            <p className="text-sm font-medium text-[var(--text-primary)]">
              {actionComplete === 'approved'
                ? 'Quote approved and finalized'
                : 'Quote sent back for revision'}
            </p>
            {reviewNotes && (
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Notes: {reviewNotes}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  // S3 — Implementations returned with an adjusted fee (emerald). Dormant until PR 4's
  // inject sets impl_review_status='reviewed'; built here so the state machine is complete.
  if (implReviewStatus === 'reviewed' && implReviewSnapshot) {
    const { feeBefore, feeAfter, completedAt, completedByName } = implReviewSnapshot
    const delta = (feeAfter ?? 0) - (feeBefore ?? 0)
    const deltaPct = feeBefore && feeBefore > 0 ? (delta / feeBefore) * 100 : 0
    const deltaSign = delta > 0 ? '+' : ''
    const deltaColour = delta > 0 ? 'text-amber-400' : delta < 0 ? 'text-emerald-400' : 'text-[var(--text-muted)]'
    return (
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CheckCircle size={20} className="text-emerald-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-[var(--text-primary)]">
              Implementations adjusted the implementation fee — review and continue
            </p>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Based on the scope hours, {completedByName || 'the Impl team'} updated the
              implementation fee for <strong>{customerName}</strong>. Check the change below,
              then continue to deal review (Jacqui &amp; Rob).
            </p>
            <div className="mt-3 flex items-center gap-3 px-3 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-md">
              <div>
                <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Before</div>
                <div className="text-sm text-[var(--text-secondary)] line-through">{formatCurrency(feeBefore ?? 0, 'AUD')}</div>
              </div>
              <ArrowRight size={14} className="text-[var(--text-muted)]" />
              <div>
                <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">After</div>
                <div className="text-sm font-semibold text-[var(--text-primary)]">{formatCurrency(feeAfter ?? 0, 'AUD')}</div>
              </div>
              <div className={`ml-auto text-sm font-medium ${deltaColour}`}>
                {deltaSign}{formatCurrency(delta, 'AUD')}
                {feeBefore && feeBefore > 0 && (
                  <span className="text-[11px] ml-1">({deltaSign}{deltaPct.toFixed(1)}%)</span>
                )}
              </div>
            </div>
            <p className="text-[11px] text-[var(--text-muted)] mt-2">
              Updated by {completedByName || 'Impl team'}{completedAt ? ` · ${getRelativeTime(completedAt)}` : ''} ·
              <a href={`/scope?session=${dealId}`} className="ml-1 text-emerald-400 hover:underline">View scope</a>
            </p>
          </div>
          <button
            onClick={async () => {
              const r = await submitForReview(Number(dealId))
              if (r.ok) onContinued?.()
            }}
            disabled={isContinuing}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-500/90 transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {isContinuing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Review change and continue
          </button>
        </div>
      </div>
    )
  }

  // S2 — Awaiting Implementations review (amber). Reached when PR 2's server divert sets
  // impl_review_status='awaiting'. Inputs are locked (Quote.tsx quoteLocks.all).
  if (implReviewStatus === 'awaiting') {
    return (
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Clock size={20} className="text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-[var(--text-primary)]">
              Awaiting Implementations review — inputs are locked
            </p>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Alitia and Aju are checking the scope hours against the proposed implementation
              fee for <strong>{customerName}</strong>. You'll be notified in Slack when they're
              done — the banner here will update too.
            </p>
            {implReviewRequestedAt && (
              <p className="text-[11px] text-[var(--text-muted)] mt-2">
                Sent for review {getRelativeTime(implReviewRequestedAt)}
                {implReviewRequestedBy ? ` by ${implReviewRequestedBy}` : ''}
              </p>
            )}
          </div>
          <a href={`/scope?session=${dealId}`} className="text-xs text-amber-400 hover:underline flex-shrink-0">
            View scope →
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-purple-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-[var(--text-primary)]">
              Quote Review Required — inputs are locked
            </p>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Reviewing quote for <strong>{customerName}</strong>. All fields below are read-only while under review so the numbers don't drift from the Slack notification. If changes are needed, send the quote back for revision.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowNotes(!showNotes)}
            className={`p-2 rounded-lg border transition-colors ${
              showNotes
                ? 'bg-[var(--bg-tertiary)] border-primary/30 text-primary'
                : 'border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
            title="Add review notes"
          >
            <MessageSquare size={16} />
          </button>

          <button
            onClick={handleReject}
            disabled={isRejecting || isApproving}
            className="flex items-center gap-1.5 px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-danger/10 hover:border-danger/30 hover:text-danger transition-colors disabled:opacity-50"
          >
            {isRejecting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <XCircle size={14} />
            )}
            Send Back
          </button>

          <button
            onClick={handleApprove}
            disabled={isApproving || isRejecting}
            className="flex items-center gap-1.5 px-4 py-2 bg-success text-white rounded-lg text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-50"
          >
            {isApproving ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <CheckCircle size={14} />
            )}
            Approve
          </button>
        </div>
      </div>

      {/* Review Notes */}
      {showNotes && (
        <div className="mt-3 pt-3 border-t border-purple-500/20">
          <textarea
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder="Add review notes (required for sending back, optional for approval)..."
            rows={3}
            className="w-full px-3 py-2 text-sm bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] resize-none focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      )}
    </div>
  )
}
